import { useEffect, useState } from 'react';
import { X, Download } from 'lucide-react';
import { api } from '../lib/api';
import { useVault } from '../../../core/lib/vault';
import { decryptJson, decryptBlob, fromB64 } from '../../../core/crypto';
import {
  categoryFor,
  iconFor,
  isTextPreviewable,
  formatSize,
} from '../lib/attachmentTypes';

interface LoadedAttachment {
  bytes: Uint8Array;
  filename: string;
  mimetype: string;
}

export function AttachmentPreviewModal({
  attachmentId,
  onClose,
}: {
  attachmentId: string;
  onClose: () => void;
}) {
  const vaultKey = useVault((s) => s.vaultKey);
  const [loaded, setLoaded] = useState<LoadedAttachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    async function run() {
      if (!vaultKey) return;
      setError(null);
      try {
        const fetched = await api.getAttachment(attachmentId);
        const meta = await decryptJson<{ filename?: string; mimetype?: string }>(
          fetched.metadata,
          vaultKey,
        );
        const bytes = await decryptBlob(fromB64(fetched.iv), fetched.blob, vaultKey);
        if (cancelled) return;
        const filename = meta.filename || `attachment-${attachmentId}`;
        const mimetype = meta.mimetype || 'application/octet-stream';
        setLoaded({ bytes, filename, mimetype });

        const cat = categoryFor(mimetype, filename);
        if (cat === 'image') {
          const blob = new Blob([bytes as BlobPart], { type: mimetype });
          createdUrl = URL.createObjectURL(blob);
          setPreviewUrl(createdUrl);
        } else if (isTextPreviewable(cat) && bytes.length < 1_000_000) {
          try {
            setTextContent(new TextDecoder('utf-8', { fatal: false }).decode(bytes));
          } catch {
            setTextContent(null);
          }
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.body?.error ?? err?.message ?? 'Could not load attachment.');
      }
    }
    void run();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachmentId, vaultKey]);

  function download() {
    if (!loaded) return;
    const blob = new Blob([loaded.bytes as BlobPart], { type: loaded.mimetype });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = loaded.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  const category = loaded ? categoryFor(loaded.mimetype, loaded.filename) : 'other';
  const Icon = iconFor(category);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="card w-full max-w-3xl max-h-[100dvh] md:max-h-[85vh] flex flex-col">
        <div className="flex items-center gap-2 border-b border-ink-800 px-4 py-3">
          <Icon size={18} className="text-dragon-500" />
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold truncate">{loaded?.filename ?? 'Loading…'}</h2>
            {loaded && (
              <p className="text-xs text-ink-500 truncate">
                {loaded.mimetype} · {formatSize(loaded.bytes.length)}
              </p>
            )}
          </div>
          <button
            onClick={download}
            className="btn-ghost"
            disabled={!loaded}
            title="Download"
          >
            <Download size={14} />
          </button>
          <button onClick={onClose} className="btn-ghost !p-1">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto bg-ink-950">
          {error && (
            <p className="p-6 text-sm text-red-400">{error}</p>
          )}
          {!error && !loaded && (
            <p className="p-6 text-sm text-ink-500">Decrypting…</p>
          )}
          {loaded && category === 'image' && previewUrl && (
            <div className="flex items-center justify-center p-4">
              <img
                src={previewUrl}
                alt={loaded.filename}
                className="max-w-full max-h-[50vh] md:max-h-[70vh] object-contain"
              />
            </div>
          )}
          {loaded && category !== 'image' && textContent != null && (
            <pre className="p-4 text-xs text-ink-200 whitespace-pre-wrap break-words font-mono">
              {textContent}
            </pre>
          )}
          {loaded && category !== 'image' && textContent == null && (
            <div className="p-6 text-sm text-ink-400 flex flex-col items-center gap-3">
              <Icon size={48} className="text-ink-500" />
              <p>Preview not available for this file type.</p>
              <button className="btn-primary" onClick={download}>
                <Download size={14} className="mr-1" />
                Download
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
